howspace.components.searchableSelect = (() => {
    'use strict';

    const COMPONENT_SELECTOR = '.howspace-searchable-select';
    const OPTION_SELECTOR = '.searchable-select-option';

    const normalize = value => String(value).trim().toLowerCase();

    const getComponent = element => element.closest(COMPONENT_SELECTOR);

    const isMultiple = searchableSelect => {
        return searchableSelect.dataset.multiple === 'true';
    };

    const getOptionValue = option => option.dataset.value ?? '';

    const getOptionLabel = option => {
        return option?.querySelector('.btn-text')?.textContent.trim() ?? option?.textContent.trim() ?? '';
    };

    const getOptions = searchableSelect => {
        return Array.from(searchableSelect.querySelectorAll(OPTION_SELECTOR));
    };

    const getSelectedValues = searchableSelect => {
        return Array.from(searchableSelect.querySelectorAll('.searchable-select-values input[type="hidden"]'))
            .map(input => input.value);
    };

    const getInputName = searchableSelect => {
        return searchableSelect.querySelector('.searchable-select-values')?.dataset.name ?? '';
    };

    const shouldMatchMenuWidth = searchableSelect => {
        return searchableSelect.dataset.matchMenuWidth === 'true';
    };

    const syncMenuWidth = searchableSelect => {
        if (!shouldMatchMenuWidth(searchableSelect)) {
            return;
        }

        const toggleButton = searchableSelect.querySelector('[data-toggle="dropdown"]');
        const menu = searchableSelect.querySelector('.howspace-menu');
        if (!toggleButton || !menu) {
            return;
        }

        const { width } = toggleButton.getBoundingClientRect();
        if (width <= 0) {
            return;
        }

        menu.style.width = `${width}px`;
        menu.style.minWidth = `${width}px`;
    };

    const createToggleText = toggleButton => {
        const toggleText = document.createElement('span');
        toggleText.classList.add('btn-text');

        const rightIcon = toggleButton.querySelector(':scope > .icon:last-child');
        if (rightIcon) {
            toggleButton.insertBefore(toggleText, rightIcon);
            return toggleText;
        }

        toggleButton.appendChild(toggleText);
        return toggleText;
    };

    const updateToggleButton = searchableSelect => {
        const toggleButton = searchableSelect.querySelector('[data-toggle="dropdown"]');
        if (!toggleButton) {
            return;
        }

        const selectedValues = getSelectedValues(searchableSelect);
        const placeholder = searchableSelect.dataset.placeholder ?? '';
        const selectedSuffix = searchableSelect.dataset.selectedSuffix ?? '';
        let text = placeholder;

        if (selectedValues.length === 1) {
            const selectedOption = getOptions(searchableSelect).find(option => getOptionValue(option) === selectedValues[0]);
            text = getOptionLabel(selectedOption) || placeholder;
        } else if (selectedValues.length > 1) {
            text = `${selectedValues.length} ${selectedSuffix}`.trim();
        }

        const toggleText = toggleButton.querySelector('.btn-text') ?? createToggleText(toggleButton);
        toggleText.textContent = text;
        toggleButton.classList.toggle('placeholder', selectedValues.length === 0);
    };

    const updateOptions = searchableSelect => {
        const selectedValues = new Set(getSelectedValues(searchableSelect));
        getOptions(searchableSelect).forEach(option => {
            const selected = selectedValues.has(getOptionValue(option));
            option.classList.toggle('active', selected);
            option.classList.toggle('selected', selected);
            option.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
    };

    const updateSelectedState = searchableSelect => {
        updateOptions(searchableSelect);
        updateToggleButton(searchableSelect);
    };

    const setSelectedValues = (searchableSelect, values) => {
        const optionValues = new Set(getOptions(searchableSelect).map(getOptionValue));
        const selectedValues = [];

        values.forEach(value => {
            value = String(value);
            if (!optionValues.has(value) || selectedValues.includes(value)) {
                return;
            }

            selectedValues.push(value);
        });

        const valuesContainer = searchableSelect.querySelector('.searchable-select-values');
        if (!valuesContainer) {
            return;
        }

        const inputName = getInputName(searchableSelect);
        valuesContainer.replaceChildren();

        selectedValues.slice(0, isMultiple(searchableSelect) ? selectedValues.length : 1).forEach(value => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.value = value;
            if (inputName !== '') {
                input.name = inputName;
            }
            valuesContainer.appendChild(input);
        });

        updateSelectedState(searchableSelect);
    };

    const getValue = searchableSelect => {
        const selectedValues = getSelectedValues(searchableSelect);
        if (isMultiple(searchableSelect)) {
            return selectedValues;
        }

        return selectedValues[0] ?? '';
    };

    const filterOptions = e => {
        if (!e.currentTarget) {
            return;
        }

        const searchableSelect = getComponent(e.currentTarget);
        if (!searchableSelect) {
            return;
        }

        const query = normalize(e.currentTarget.value);
        let visibleOptions = 0;

        getOptions(searchableSelect).forEach(option => {
            const searchText = normalize(option.dataset.searchText ?? getOptionLabel(option));
            const visible = searchText.includes(query);
            option.classList.toggle('d-none', !visible);
            if (visible) {
                visibleOptions += 1;
            }
        });

        searchableSelect.querySelector('.searchable-select-empty')?.classList.toggle('d-none', visibleOptions !== 0);
    };

    const clearSearch = (searchableSelect, focusInput = false) => {
        const searchInput = searchableSelect.querySelector('.howspace-form-search-input');
        if (!searchInput) {
            return;
        }

        searchInput.value = '';
        $(searchInput).trigger('input');

        if (focusInput) {
            searchInput.focus();
        }
    };

    const handleOptionClick = e => {
        const option = e.currentTarget;
        const searchableSelect = getComponent(option);
        const selectedValues = getSelectedValues(searchableSelect);
        const value = getOptionValue(option);

        if (isMultiple(searchableSelect)) {
            e.preventDefault();
            e.stopPropagation();

            if (selectedValues.includes(value)) {
                setSelectedValues(searchableSelect, selectedValues.filter(selectedValue => selectedValue !== value));
            } else {
                setSelectedValues(searchableSelect, [...selectedValues, value]);
            }

            clearSearch(searchableSelect, true);
        } else {
            setSelectedValues(searchableSelect, [value]);
            clearSearch(searchableSelect);
            howspace.components.dropdown.close(searchableSelect);
            searchableSelect.querySelector('[data-toggle="dropdown"]')?.focus();
        }

        howspace.dispatchEvent(searchableSelect, howspace.components.searchableSelect.CHANGE, {
            value: getValue(searchableSelect),
            values: getSelectedValues(searchableSelect)
        });
    };

    const focusSearchAfterOpen = searchableSelect => {
        setTimeout(() => {
            if (searchableSelect.classList.contains('expanded')) {
                searchableSelect.querySelector('.howspace-form-search-input')?.focus();
            }
        });
    };

    const handlePopoverOpened = e => {
        const searchableSelect = e.currentTarget;
        focusSearchAfterOpen(searchableSelect);
        syncMenuWidth(searchableSelect);

        if (!shouldMatchMenuWidth(searchableSelect)) {
            return;
        }

        const menu = searchableSelect.querySelector('.howspace-menu');
        if (menu) {
            howspace.popover.open(searchableSelect, menu, menu.querySelector('.menu-items'));
        }
    };

    return {
        CHANGE: 'searchable-select-change',
        init: elem => {
            elem.findIncludingSelf(`${COMPONENT_SELECTOR}:not(.howspace-searchable-select-initialized)`).each(function () {
                const searchableSelect = $(this);
                searchableSelect.addClass('howspace-searchable-select-initialized');

                updateSelectedState(this);
                filterOptions({ currentTarget: this.querySelector('.howspace-form-search-input') });

                searchableSelect.on('click', OPTION_SELECTOR, handleOptionClick);
                searchableSelect.on(howspace.components.searchInput.SEARCH, '.howspace-form-search-input', e => {
                    filterOptions({ currentTarget: e.target });
                });

                // Prevent SearchInput interactions from collapsing the dropdown or triggering dropdown key handlers.
                searchableSelect.on('click', '.clear-search-btn', e => {
                    e.stopPropagation();
                });

                searchableSelect.on('keydown', '.howspace-form-search-input', e => {
                    if (e.key === 'Escape') {
                        if (e.currentTarget.value.trim() !== '') {
                            e.currentTarget.dataset.escapeWillOnlyClear = 'true';
                            e.stopPropagation();
                            return;
                        }

                        delete e.currentTarget.dataset.escapeWillOnlyClear;
                    }

                    if (e.key === 'Home' || e.key === 'End') {
                        e.stopPropagation();
                    }
                });

                searchableSelect.on('keyup', '.howspace-form-search-input', e => {
                    if (e.key !== 'Escape') {
                        return;
                    }

                    if (e.currentTarget.dataset.escapeWillOnlyClear === 'true') {
                        delete e.currentTarget.dataset.escapeWillOnlyClear;
                        e.stopPropagation();
                        return;
                    }

                    howspace.components.dropdown.close(searchableSelect);
                    searchableSelect.find('[data-toggle="dropdown"]')[0]?.focus();
                    e.stopPropagation();
                });

                searchableSelect.on(howspace.popover.POPOVER_OPENED, handlePopoverOpened);
            });
        },
        destroy: elem => {
            elem.findIncludingSelf(`${COMPONENT_SELECTOR}.howspace-searchable-select-initialized`).each(function () {
                const searchableSelect = $(this);
                searchableSelect.removeClass('howspace-searchable-select-initialized');

                searchableSelect.off('click', OPTION_SELECTOR, handleOptionClick);
                searchableSelect.off(howspace.components.searchInput.SEARCH, '.howspace-form-search-input');
                searchableSelect.off('click', '.clear-search-btn');
                searchableSelect.off('keydown', '.howspace-form-search-input');
                searchableSelect.off('keyup', '.howspace-form-search-input');
                searchableSelect.off(howspace.popover.POPOVER_OPENED, handlePopoverOpened);
            });
        },
        getValue: searchableSelect => {
            if (searchableSelect instanceof jQuery) {
                if (searchableSelect.length === 0) {
                    return '';
                }
                searchableSelect = searchableSelect[0];
            }

            searchableSelect = howspace.getDOMElement(searchableSelect);
            if (!searchableSelect) {
                return '';
            }

            return getValue(searchableSelect);
        },
        setValue: (searchableSelect, value) => {
            if (searchableSelect instanceof jQuery) {
                if (searchableSelect.length === 0) {
                    return;
                }
                searchableSelect = searchableSelect[0];
            }

            searchableSelect = howspace.getDOMElement(searchableSelect);
            if (!searchableSelect) {
                return;
            }

            if (value === null || value === undefined) {
                setSelectedValues(searchableSelect, []);
                return;
            }

            setSelectedValues(searchableSelect, Array.isArray(value) ? value : [value]);
        }
    };
})();
